import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslations } from '../hooks/useTranslations';
import { getLanguage } from '@deadpan/shared';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  noindex?: boolean; // Prevent search engines from indexing this page
}

function normalizePublicSiteUrl(rawUrl: string): string {
  if (!rawUrl) return rawUrl;
  try {
    const parsed = new URL(rawUrl);
    if (parsed.hostname === 'msgames26.com') {
      parsed.hostname = 'www.msgames26.com';
    }
    return parsed.toString();
  } catch {
    return rawUrl;
  }
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  image = '/og-image.png',
  url = typeof window !== 'undefined' ? window.location.href : '',
  type = 'website',
  noindex = false,
}) => {
  const t = useTranslations();
  const lang = getLanguage();
  
  const defaultTitle = lang === 'zh' 
    ? 'MS GAMES - 话游，用说话玩的游戏'
    : 'MS GAMES - Games you play by talking';
  
  const defaultDescription = lang === 'zh' 
    ? '话游，用说话玩的游戏。在线玩一本正经、谁是卧底、炸弹接力、冥想盆、言为心声，另有抽签小工具。3-8 人即可开玩，无需注册。'
    : 'Games you play by talking. Play Deadpan, Undercover, Bomb Relay, Pensieve, and Words From the Heart online. Draw lots included. 3-8 players, no registration required.';
  
  const finalTitle = title || defaultTitle;
  const finalDescription = description || defaultDescription;
  const siteUrl = typeof window !== 'undefined'
    ? normalizePublicSiteUrl(`${window.location.protocol}//${window.location.host}`)
    : '';
  const imageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;
  const finalUrl = normalizePublicSiteUrl(url.startsWith('http') ? url : `${siteUrl}${url}`);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta name="keywords" content={lang === 'zh' 
        ? 'MS GAMES,话游,用说话玩的游戏,派对游戏,聚会游戏,一本正经,谁是卧底,炸弹接力,冥想盆,言为心声,瞎掰王,大话王,抽签,在线游戏,免注册'
        : 'MS GAMES,games you play by talking,party game,deadpan,undercover,bomb relay,pensieve,fictionary,draw lots,online game,no registration'} />
      <link rel="canonical" href={finalUrl} />
      
      {/* Language and Locale */}
      <html lang={lang === 'zh' ? 'zh-CN' : 'en'} />
      <link rel="alternate" hrefLang="en" href={`${siteUrl}?lang=en`} />
      <link rel="alternate" hrefLang="zh" href={`${siteUrl}?lang=zh`} />
      <link rel="alternate" hrefLang="x-default" href={siteUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={lang === 'zh' ? 'zh_CN' : 'en_US'} />
      <meta property="og:site_name" content={t.ui.gameTitle} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={finalUrl} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={imageUrl} />
      
      {/* Structured Data - WebApplication */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: t.ui.gameTitle,
          description: finalDescription,
          applicationCategory: 'Game',
          operatingSystem: 'Web Browser',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
          },
          inLanguage: lang === 'zh' ? 'zh-CN' : 'en',
        })}
      </script>
      
      {/* Structured Data - Game / WebSite */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'MS GAMES',
          alternateName: lang === 'zh' 
            ? ['MS GAMES 话游', '一本正经', '谁是卧底', '炸弹接力', '冥想盆', '言为心声']
            : ['MS GAMES', 'Deadpan', 'Undercover', 'Bomb Relay', 'Pensieve', 'Words From the Heart'],
          description: finalDescription,
          url: finalUrl,
          inLanguage: lang === 'zh' ? 'zh-CN' : 'en',
          potentialAction: {
            '@type': 'SearchAction',
            target: { '@type': 'EntryPoint', urlTemplate: `${siteUrl}/?lang=${lang}` },
            'query-input': 'required name=search_term_string',
          },
        })}
      </script>
    </Helmet>
  );
};

